<script lang="ts">
  import type { Segment } from '$lib/stores/aiFeatures'

  /**
   * AICardController: Handles AI mode card display and actions
   * Single Responsibility: Manage AI segments display and processing
   */

  interface Props {
    aiSegments: Segment[]
    aiMode: 'paraphrase' | 'expand' | 'shrink'
    isProcessing?: boolean
    onSegmentHover?: (segmentId: string | null) => void
  }

  let { 
    aiSegments = [],
    aiMode = 'paraphrase',
    isProcessing = false,
    onSegmentHover
  }: Props = $props()

  // Calculate active segments (segments with changes, including applied)
  let activeSegments = $derived.by(() => {
    const segments = aiSegments.filter(s => s.hasChanges)
    return segments
  })
  
  let hasActiveSegments = $derived(activeSegments.length > 0)
  
  let hasUnprocessedSegments = $derived(
    aiSegments.some(seg => seg.hasChanges && seg.status !== 'applied' && seg.status !== 'skipped')
  )
  
  let hasAppliedSegments = $derived(
    aiSegments.some(s => s.status === 'applied')
  )

  // Check if any segments are processing
  let isSegmentProcessing = $derived(
    aiSegments.some(seg => seg.isProcessing === true)
  )
  
  // Export getters for derived values
  export function getActiveSegments() {
    return activeSegments
  }
  
  export function getHasActiveSegments() {
    return hasActiveSegments
  }
  
  export function getHasUnprocessedSegments() {
    return hasUnprocessedSegments
  }
  
  export function getHasAppliedSegments() {
    return hasAppliedSegments
  }
  
  export function getIsSegmentProcessing() {
    return isSegmentProcessing
  }

  // Get mode title
  export function getAIModeTitle(): string {
    switch (aiMode) {
      case 'expand': return '내용 확장'
      case 'shrink': return '내용 축소'
      case 'paraphrase': return '바꿔쓰기'
      default: return 'AI 개선'
    }
  }

  // Get segment counts
  let appliedCount = $derived(
    aiSegments.filter(seg => seg.status === 'applied').length
  )
  
  let skippedCount = $derived(
    aiSegments.filter(seg => seg.status === 'skipped').length
  )

  // Check if all segments are processed
  let allProcessed = $derived(
    aiSegments.length > 0 && 
    aiSegments.every(seg => seg.status === 'applied' || seg.status === 'skipped')
  )
  
  // Export getters for counts
  export function getAppliedCount() {
    return appliedCount
  }
  
  export function getSkippedCount() {
    return skippedCount
  }
  
  export function getAllProcessed() {
    return allProcessed
  }

  // Clear hover state
  export function clearHoverState() {
    onSegmentHover?.(null)
  }
</script>